import { Link, useLocation } from "react-router";
import {
  Users,
  Briefcase,
  Building2,
  Star,
  Wallet,
  Settings as SettingsIcon,
  Menu,
  X,
  LogOut,
  Home,
  FileText,
  ShieldCheck,
  Clock,
  ClipboardCheck,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { drawerVisibilityClass, useMobileDrawer } from "@/hooks/useMobileDrawer";
import { getUserDisplayName } from "@/lib/auth";
import { isComplianceEnabled } from "@/lib/features";
import { ADMIN_COMPLIANCE_PATH } from "@/lib/compliance";
import { Button } from "@/components/ui/button";
import { ConsultantAvatar } from "@/components/consultants/ConsultantAvatar";
import Version from "@/components/Version";
import { EnvironmentHeaderMark } from "@/components/environment/EnvironmentBadge";
import { environmentMarkLabel } from "@/lib/environment";
import { ENV_HEADER_ACCENT_CLASS, ENV_MOBILE_TOGGLE_TOP_CLASS } from "@/lib/environmentMarkStyles";

const allNavItems = [
  {
    title: "Main Website",
    path: "/",
    icon: Home,
    description: "Return to the main website",
  },
  {
    title: "Joiners",
    path: "/admin/consultants/applications",
    icon: FileText,
    description: "Manage new consultant join requests",
  },
  {
    title: "Consultants",
    path: "/admin/consultants",
    icon: Users,
    description: "Manage registered consultants and profiles",
  },
  {
    title: "Companies",
    path: "/admin/companies",
    icon: Building2,
    description: "Manage client companies and organisations",
  },
  {
    title: "Assignments",
    path: "/admin/assignments",
    icon: Briefcase,
    description: "Create and manage consultant assignments",
  },
  {
    title: "Skills",
    path: "/admin/skills",
    icon: Star,
    description: "Manage available skills and categories",
  },
  {
    title: "Timesheets",
    path: "/admin/timesheets",
    icon: Clock,
    description: "Review and approve consultant timesheets",
  },
  {
    title: "Compliance",
    path: ADMIN_COMPLIANCE_PATH,
    icon: ClipboardCheck,
    description: "Tasks, agreements and group policies",
  },
  {
    title: "Permissions",
    path: "/admin/permissions",
    icon: ShieldCheck,
    description: "Manage user roles and access control",
  },
  {
    title: "Financials",
    path: "/admin/financials",
    icon: Wallet,
    description: "Handle invoices and financial reports",
  },
  {
    title: "Site Config",
    path: "/admin/config",
    icon: SettingsIcon,
    description: "Configure system settings",
  },
];

const AdminSidebar = () => {
  const { isOpen, close, drawerId, drawerRef, toggleProps } = useMobileDrawer();
  const { user, logout } = useAuth();
  const location = useLocation();
  // Non-production marks (#1099); PROD keeps the exact classes below.
  const envMarked = environmentMarkLabel() !== null;
  // Compliance is behind the compliance feature flag (BE #898).
  const compliance = isComplianceEnabled(user);
  const navItems = allNavItems.filter(item => compliance || item.path !== ADMIN_COMPLIANCE_PATH);

  // Highlight exactly one item: the most-specific path that matches the current
  // location. Longest match wins so Joiners (/admin/consultants/applications)
  // beats Consultants (/admin/consultants), and Main Website ("/") only lights
  // up on the main site — never on every /admin/* page (issue #741).
  const activePath = navItems
    .filter(item =>
      item.path === "/"
        ? location.pathname === "/"
        : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
    )
    .reduce<string | null>(
      (best, item) => (item.path.length > (best?.length ?? 0) ? item.path : best),
      null
    );

  const isActive = (path: string) => path === activePath;

  return (
    <>
      {/* Mobile Navigation Toggle */}
      <div
        className={`fixed ${envMarked ? ENV_MOBILE_TOGGLE_TOP_CLASS : "top-4"} left-4 z-50 md:hidden`}
      >
        <Button
          variant="outline"
          size="icon"
          {...toggleProps}
          className="bg-background/80 backdrop-blur-sm"
        >
          {isOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
        </Button>
      </div>

      {/* Sidebar Navigation */}
      <aside
        id={drawerId}
        ref={drawerRef}
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-sidebar border-r border-sidebar-border shadow-lg transform ${drawerVisibilityClass(
          isOpen
        )} md:relative md:shrink-0 flex flex-col h-full overflow-y-auto`}
      >
        <div
          className={`p-4 border-b border-sidebar-border flex items-center justify-between${
            envMarked ? ` ${ENV_HEADER_ACCENT_CLASS}` : ""
          }`}
        >
          <Link to="/admin" className="flex items-center space-x-3">
            <div className="w-8 h-8 bondit-bg-primary rounded-lg flex items-center justify-center text-white font-bold">
              A
            </div>
            <span className="text-lg font-bold text-sidebar-foreground">Admin Portal</span>
          </Link>
          <EnvironmentHeaderMark />
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              aria-current={isActive(item.path) ? "page" : undefined}
              className={`nav-link flex items-center space-x-3 py-3 px-4 rounded-lg transition-colors ${
                isActive(item.path)
                  ? "bg-blue-100 text-blue-800 font-medium shadow-sm"
                  : "hover:bg-sidebar-accent/50 text-sidebar-foreground"
              }`}
              onClick={close}
            >
              <item.icon className={`h-5 w-5 ${isActive(item.path) ? "text-blue-700" : ""}`} />
              <div>
                <div>{item.title}</div>
                <div className="text-xs text-muted-foreground">{item.description}</div>
              </div>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-sidebar-border">
          <div className="flex items-center space-x-3 px-3 py-2 mb-2">
            <ConsultantAvatar
              consultantId={user?.consultant_profile?.id}
              name={getUserDisplayName(user)}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{getUserDisplayName(user)}</p>
              <p className="text-xs text-sidebar-foreground/70 truncate capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="w-full justify-start text-sidebar-foreground/70 hover:text-sidebar-foreground"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
          <div className="flex justify-center mt-2 pt-2 border-t border-sidebar-border/50">
            <Version className="text-xs text-sidebar-foreground/50 hover:text-sidebar-foreground/80" />
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm transition-opacity duration-300 appearance-none border-0 p-0"
          onClick={close}
        />
      )}
    </>
  );
};

export default AdminSidebar;
